import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, Signal } from '@angular/core';
import { collapseAnimation } from '@myarvato/shared/animations';
import { DayjsPipe } from '@myarvato/shared/pipelines';
import { BreakpointService } from '@myarvato/shared/services';
import { TranslatePipe } from '@ngx-translate/core';
import { Timeline } from 'primeng/timeline';
import { DetailViewDto, MilestoneDto } from '@myarvato/modules/transport/models';
import { MilestoneEvent } from './models/milestone-event.model';

@Component({
  selector: 'transport-details-milestones',
  imports: [CommonModule, TranslatePipe, Timeline],
  templateUrl: './details-milestones.component.html',
  styleUrl: './details-milestones.component.scss',
  animations: [collapseAnimation],
  providers: [DayjsPipe],
})
export class DetailsMilestonesComponent {
  // Input property for the detail view
  public readonly details = input.required<DetailViewDto>();

  // Injected services
  private readonly dayjsPipe = inject(DayjsPipe);
  private readonly breakpointService = inject(BreakpointService);

  // Consistent mapping of progress to display labels
  private readonly progressLabels: Record<string, string> = {
    READY: 'Ready for shipping',
    INTRANSIT: 'In transit',
    WAITING: 'Waiting for customer pick up',
    DELIVERED: 'Delivered',
    RET_SENDER: 'Returning to Sender',
    RETURN: 'Returned',
  };

  // Fixed milestone order (will be filtered dynamically)
  private readonly milestoneOrder = ['READY', 'INTRANSIT', 'WAITING', 'DELIVERED', 'RET_SENDER', 'RETURN'];

  // Compute timeline layout based on breakpoint
  protected readonly timelineLayout = computed(() => {
    const breakpoint = this.breakpointService.currentBreakpoint();
    return breakpoint === 'xs' || breakpoint === 'sm' ? 'vertical' : 'horizontal';
  });

  protected readonly timelineAlign = computed(() => {
    return this.timelineLayout() === 'vertical' ? 'right' : 'top';
  });

  // Compute events to display in timeline
  protected readonly events: Signal<MilestoneEvent[]> = computed(() => {
    const milestones = this.details().milestones ?? [];

    const hasWaiting = milestones.some((milestone) => milestone.progressCode === 'WAITING');
    const hasReturningToSender = milestones.some((milestone) => milestone.progressCode === 'RET_SENDER');
    const hasReturned = milestones.some((milestone) => milestone.progressCode === 'RETURN');

    // Determine applicable milestones dynamically
    const applicableCodes = this.milestoneOrder.filter((code) => {
      switch (code) {
        case 'WAITING':
          return hasWaiting;
        case 'RET_SENDER':
          return hasReturningToSender;
        case 'RETURN':
          return hasReturningToSender || hasReturned;
        case 'DELIVERED':
          return !hasReturningToSender && !hasReturned;
        default:
          return true;
      }
    });

    // Map progressCode → milestone
    const codeToMilestone = new Map<string, MilestoneDto>();
    for (const milestone of milestones) {
      if (milestone.progressCode && !codeToMilestone.has(milestone.progressCode)) {
        codeToMilestone.set(milestone.progressCode, milestone);
      }
    }

    // Build final milestone events
    const result: MilestoneEvent[] = applicableCodes.map((code) => {
      const milestone = codeToMilestone.get(code);
      const description = this.formatMilestoneDescription(milestone);

      if (milestone) {
        return {
          headline: this.progressLabels[code],
          description,
          icon: 'pi pi-check-circle',
          severity: 'success',
        };
      } else {
        return {
          headline: this.progressLabels[code],
          description: '',
          icon: 'pi pi-circle',
          severity: 'default',
        };
      }
    });

    // Reverse for vertical layout to show event history correctly
    return this.timelineLayout() === 'vertical' ? [...result].reverse() : result;
  });

  // Format timestamp with timezone if available
  private formatMilestoneDescription(milestone?: MilestoneDto): string {
    if (!milestone?.timestamp) {
      return '';
    }

    if (milestone.timezone) {
      return `${this.dayjsPipe.transform(milestone.timestamp, 'L LT', false, false)} (${milestone.timezone})`;
    }

    return this.dayjsPipe.transform(milestone.timestamp, 'L LT', true);
  }
}
